import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import {
  REFERENCE_PERIODS,
  evaluate,
  vehicleFactsOf,
  type ComplianceGrouping,
  type FrameworkAgreement,
  type Vehicle,
} from '@dashboard/contracts';
import {
  useAllFleetAgreements,
  useAllFleetAssignments,
  useAllFleetVehicles,
  useFleetCompliance,
  useFleetSummary,
  useParties,
  useProcurementUnits,
  useWithdrawAssignment,
} from '../api/queries';
import { useSession } from '../auth/session-context';
import { formatDate, formatNumber, todayIsoDate } from '../i18n';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/states';
import { TabPanel, Tabs } from '../components/ui/selection-controls';
import {
  AgreementStatusBadge,
  CleanVerdictBadge,
  VerificationBadge,
} from '../components/ui/status-badge';
import { AgreementDialog } from '../components/fleet/agreement-dialog';
import { DeclareDialog } from '../components/fleet/declare-dialog';
import { VehicleDialog } from '../components/fleet/vehicle-dialog';
import { energyLabelKey } from '../components/fleet/fleet-labels';
import { RulesReference } from '../components/fleet/rules-reference';
import { GroupShareBar } from '../components/fleet/share-bar';

/**
 * Clean-vehicle compliance.
 *
 * Not nested under `ProjectGate`, and the first screen in the product that is
 * not: a framework agreement for waste collection is not part of an
 * infrastructure project, so this is addressed by organization alone.
 *
 * Three tabs behind three permissions, in the shared `Tabs` control every
 * other switcher in the product now uses -- the same control because it is the
 * same question, and a second way of switching between views of one subject
 * would be a worse outcome than either way alone.
 */

type Tab = 'vehicles' | 'agreements' | 'compliance' | 'rules';

export function FleetPage() {
  const { t } = useTranslation();
  const { canAny } = useSession();
  const [params, setParams] = useSearchParams();

  const dataTabs: Tab[] = [
    ...(canAny('fleet.read') ? (['vehicles'] as const) : []),
    ...(canAny('fleet.agreement.read') ? (['agreements'] as const) : []),
    ...(canAny('fleet.report.read') ? (['compliance'] as const) : []),
  ];
  /* The rules carry no permission of their own -- they are reference material,
     the same for every reader, and a catalog somebody is refused is a catalog
     they go and read in the source instead. They are still only offered beside
     something to apply them to, so a reader with no fleet rights at all gets
     the permission notice rather than a lone glossary. */
  const tabs: Tab[] = dataTabs.length > 0 ? [...dataTabs, 'rules'] : [];
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && tabs.includes(requested) ? requested : (tabs[0] ?? 'vehicles');

  if (tabs.length === 0) {
    return <EmptyState title={t('state.permissionTitle')} body={t('state.permissionBody')} />;
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('fleet.title')}</h1>
          <p>{t('fleet.subtitle')}</p>
        </div>
      </header>

      <Tabs
        items={tabs.map((candidate) => ({
          value: candidate,
          label: t(`fleet.tabs.${candidate}`),
        }))}
        value={tab}
        onChange={(candidate) => setParams({ tab: candidate }, { replace: true })}
        label={t('fleet.title')}
        idPrefix="fleet"
      />

      <TabPanel idPrefix="fleet" value={tab}>
        {tab === 'vehicles' ? <VehiclesTab /> : null}
        {tab === 'agreements' ? <AgreementsTab /> : null}
        {tab === 'compliance' ? <ComplianceTab /> : null}
        {tab === 'rules' ? <RulesReference /> : null}
      </TabPanel>
    </div>
  );
}

/* ------------------------------------------------------------- vehicles */

/**
 * The register, with the verdict computed in the browser.
 *
 * From the same catalog the API reads, which is the point of the rules living
 * in `@dashboard/contracts`: the date control re-judges the list without a
 * round trip, and the answer it gives is the answer the report will give.
 * Setting it to 2025-12-31 and then to 2026-01-01 is the fastest way to see
 * what the second reference period does to a fleet.
 */
function VehiclesTab() {
  const { t, i18n } = useTranslation();
  const { canAny, isExternal, session } = useSession();
  const canManage = canAny('fleet.manage');

  const [asOf, setAsOf] = useState(() => todayIsoDate());
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [adding, setAdding] = useState(false);

  const query = useAllFleetVehicles();
  /* For a supplier the server counts only their own vehicles
     (docs/organizations-design.md section 3.3), so everybody's tiles read the
     same summary. */
  const summary = useFleetSummary();
  /* The operator picker reads the organization register. A supplier's
     vehicles are always their own, so they get no picker and no request. */
  const parties = useParties(canManage && !isExternal && canAny('party.read'));

  if (query.isPending) return <LoadingState rows={5} />;
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const vehicles = query.items;

  const verdictOf = (vehicle: Vehicle) => evaluate(vehicleFactsOf(vehicle), asOf);

  const judged = vehicles.map((vehicle) => ({ vehicle, verdict: verdictOf(vehicle) }));
  const clean = judged.filter((row) => row.verdict.verdict === 'clean').length;
  const undetermined = judged.filter((row) => row.verdict.verdict === 'indeterminate').length;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('fleet.tabs.vehicles')}</h2>
          <div className="card-actions">
            <label className="fleet-control" htmlFor="fleet-as-of">
              <span>{t('fleet.vehicles.asOf')}</span>
              <input
                id="fleet-as-of"
                className="input"
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
              />
            </label>
            {/* Beside the control that changes every verdict in the table,
                because that is where somebody first wonders what is doing the
                judging. The date control teaches that the answer moves; this
                is the only thing on the screen that says what moved it. */}
            <Link className="button button--ghost" to="/fleet?tab=rules">
              {t('fleet.vehicles.howJudged')}
            </Link>
            {canManage ? (
              <button
                type="button"
                className="button button--primary"
                onClick={() => setAdding(true)}
              >
                {t('fleet.vehicles.add')}
              </button>
            ) : null}
          </div>
        </div>

        {isExternal ? (
          <div className="card-body">
            <p className="cell-secondary">
              {t('fleet.vehicles.scopedTo', { organization: session.memberOf.name })}
            </p>
          </div>
        ) : null}

        {/* The numbers a reader wants before the table: how many, how many
            count, and how many nobody can judge. The third drives work, which
            is why it is a peer of the other two rather than a footnote. */}
        <div className="card-body">
          <div className="kpi-row">
            <div className="kpi-tile">
              <span className="kpi-tile__label">{t('fleet.vehicles.inService')}</span>
              {/* Counted in the database, not from the rows on screen: this is
                  a claim about the fleet, and a claim about the fleet must not
                  be computed from however much of it happens to be loaded. */}
              <span className="kpi-tile__value">
                {summary.data
                  ? formatNumber(summary.data.totalVehicles, i18n.language)
                  : formatNumber(vehicles.length, i18n.language)}
              </span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile__label">{t('fleet.verdict.clean')}</span>
              {/* These two cannot be counted in SQL -- the verdict depends on
                  the date above -- so they are counted here, and shown as an
                  em dash until every page has arrived rather than as a number
                  that is about to change. */}
              <span className="kpi-tile__value">
                {query.isComplete ? formatNumber(clean, i18n.language) : '—'}
              </span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile__label">{t('fleet.verdict.indeterminate')}</span>
              <span className="kpi-tile__value">
                {query.isComplete ? formatNumber(undetermined, i18n.language) : '—'}
              </span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile__label">{t('fleet.vehicles.declared')}</span>
              <span className="kpi-tile__value">
                {summary.data ? formatNumber(summary.data.declaredVehicles, i18n.language) : '—'}
              </span>
            </div>
          </div>
        </div>

        <div className="card-body card-body--flush">
          {vehicles.length === 0 ? (
            <EmptyState body={t('fleet.vehicles.empty')} />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t('fleet.vehicles.plate')}</th>
                    <th scope="col">{t('fleet.vehicles.category')}</th>
                    <th scope="col">{t('fleet.vehicles.energy')}</th>
                    <th scope="col">{t('fleet.vehicles.verdict')}</th>
                    <th scope="col">{t('fleet.vehicles.party')}</th>
                    <th scope="col" className="numeric">
                      {t('fleet.vehicles.declarations')}
                    </th>
                    {canManage ? (
                      <th scope="col">
                        <span className="visually-hidden">{t('fleet.vehicles.actions')}</span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {judged.map(({ vehicle, verdict }) => (
                    <tr key={vehicle.id}>
                      <td>
                        <strong>{vehicle.registrationPlate}</strong>
                        {vehicle.withdrawnOn ? (
                          <div className="cell-secondary">{t('fleet.vehicleStatus.withdrawn')}</div>
                        ) : null}
                      </td>
                      <td>{vehicle.category}</td>
                      <td>
                        {vehicle.energySources
                          .map((source) => t(energyLabelKey(source)))
                          .join(', ')}
                      </td>
                      <td>
                        <CleanVerdictBadge verdict={verdict.verdict} />
                        {/* The reason beside the verdict, always. "Cannot be
                            judged" alone sends somebody to stare at the vehicle;
                            "no CO2 figure recorded" sends them to the empty
                            field. */}
                        <div className="cell-secondary">{t(verdict.reasonKey)}</div>
                      </td>
                      <td>{vehicle.party?.name ?? t('fleet.vehicleForm.ownFleet')}</td>
                      <td className="numeric">
                        {formatNumber(vehicle.assignmentCount, i18n.language)}
                      </td>
                      {canManage ? (
                        <td>
                          <button
                            type="button"
                            className="button button--ghost"
                            /* Named by the row it acts on. Twelve buttons all
                               announced as "Edit" is a list a screen-reader
                               user cannot navigate, and the plate is what
                               distinguishes them. */
                            aria-label={t('fleet.vehicles.editNamed', {
                              plate: vehicle.registrationPlate,
                            })}
                            onClick={() => setEditing(vehicle)}
                          >
                            {t('fleet.actions.edit')}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {adding ? (
        <VehicleDialog
          vehicle={null}
          parties={parties.data ?? []}
          operatorFixed={isExternal ? session.memberOf.name : null}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {editing ? (
        <VehicleDialog
          vehicle={editing}
          parties={parties.data ?? []}
          operatorFixed={isExternal ? session.memberOf.name : null}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

/* ----------------------------------------------------------- agreements */

function AgreementsTab() {
  const { t, i18n } = useTranslation();
  const { canAny } = useSession();
  const canManage = canAny('fleet.agreement.manage');
  const canDeclare = canAny('fleet.disclose');

  const [editing, setEditing] = useState<FrameworkAgreement | null>(null);
  const [adding, setAdding] = useState(false);
  const [declaringOn, setDeclaringOn] = useState<FrameworkAgreement | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useAllFleetAgreements();
  const units = useProcurementUnits(canManage);
  const partiesQuery = useParties(canManage);
  /* Suppliers: every organization but the client itself, which does not
     award agreements to itself. */
  const suppliers = (partiesQuery.data ?? []).filter((party) => !party.isHost);

  if (query.isPending) return <LoadingState rows={5} />;
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const agreements = query.items;

  /*
   * An agreement names a procurement unit and a supplier, so neither list may
   * be empty -- and when one is, the page says so in words, with the way to fix
   * it.
   *
   * The first version disabled the button and put the reason in a `title`,
   * which browsers do not show on a disabled control, so on a fresh deployment
   * the button was simply dead. It also checked only for units, so with units
   * and no suppliers the form opened on an empty supplier picker; and the place
   * its tooltip sent people -- Settings -> Clean vehicles -- had no way to
   * create a unit at all. The seed creates both, which is how it shipped.
   */
  const missingUnits = canManage && units.isSuccess && units.data.length === 0;
  const missingSuppliers = canManage && partiesQuery.isSuccess && suppliers.length === 0;
  const setupIncomplete = missingUnits || missingSuppliers;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{t('fleet.tabs.agreements')}</h2>
          <div className="card-actions">
            {canManage ? (
              <button
                type="button"
                className="button button--primary"
                onClick={() => setAdding(true)}
                /* Disabled while the lists it needs are loading or empty; the
                   notice below says which, rather than a tooltip nobody sees. */
                disabled={setupIncomplete || units.isPending || partiesQuery.isPending}
              >
                {t('fleet.agreements.add')}
              </button>
            ) : null}
          </div>
        </div>

        {setupIncomplete ? (
          <div className="card-body">
            <div className="fleet-note fleet-note--warning" role="note">
              <strong>{t('fleet.agreements.setupTitle')}</strong>
              <ul className="fleet-setup-list">
                {missingUnits ? (
                  <li>
                    {t('fleet.agreements.needsUnits')}{' '}
                    <Link to="/settings/fleet">{t('settings.sections.fleet.title')}</Link>
                  </li>
                ) : null}
                {missingSuppliers ? (
                  <li>
                    {t('fleet.agreements.needsSuppliers')}{' '}
                    <Link to="/users?tab=organizations">{t('users.tabOrganizations')}</Link>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="card-body card-body--flush">
          {agreements.length === 0 ? (
            <EmptyState body={t('fleet.agreements.empty')} />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t('fleet.agreements.reference')}</th>
                    <th scope="col">{t('fleet.agreements.title')}</th>
                    <th scope="col">{t('fleet.agreements.unit')}</th>
                    <th scope="col">{t('fleet.agreements.supplier')}</th>
                    <th scope="col">{t('fleet.agreements.awardedOn')}</th>
                    <th scope="col">{t('fleet.agreements.period')}</th>
                    <th scope="col">{t('fleet.agreements.scope')}</th>
                    <th scope="col" className="numeric">
                      {t('fleet.agreements.vehicles')}
                    </th>
                    <th scope="col">
                      <span className="visually-hidden">{t('fleet.vehicles.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.map((agreement) => (
                    <tr key={agreement.id}>
                      <td>{agreement.reference ?? agreement.key}</td>
                      <td>
                        <strong>{agreement.title}</strong>
                        <div className="cell-secondary">
                          <AgreementStatusBadge status={agreement.status} />
                        </div>
                      </td>
                      <td>{agreement.procurementUnit.name}</td>
                      <td>{agreement.supplierParty.name}</td>
                      <td>
                        {agreement.awardedOn ? formatDate(agreement.awardedOn, i18n.language) : '—'}
                      </td>
                      <td>
                        {agreement.referencePeriodId
                          ? t(
                              agreement.referencePeriodId === '2021-2025'
                                ? 'fleet.period.first'
                                : 'fleet.period.second',
                            )
                          : '—'}
                      </td>
                      <td>
                        {agreement.inScope
                          ? t('fleet.agreements.inScope')
                          : t('fleet.agreements.outOfScope')}
                        {/* An override is shown as an override, with its reason,
                            rather than collapsed into the effective answer: a
                            scope decision made by hand is the one a reader most
                            needs to be able to question. */}
                        {agreement.inScopeOverride !== null ? (
                          <div className="cell-secondary">
                            {t('fleet.agreements.overridden', {
                              reason: agreement.scopeOverrideReason ?? '',
                            })}
                          </div>
                        ) : null}
                      </td>
                      <td className="numeric">
                        <button
                          type="button"
                          className="button button--ghost"
                          aria-expanded={expanded === agreement.id}
                          onClick={() =>
                            setExpanded(expanded === agreement.id ? null : agreement.id)
                          }
                        >
                          {formatNumber(agreement.assignmentCount, i18n.language)}
                        </button>
                      </td>
                      <td>
                        {canDeclare ? (
                          <button
                            type="button"
                            className="button"
                            aria-label={t('fleet.declare.actionNamed', {
                              title: agreement.title,
                            })}
                            onClick={() => setDeclaringOn(agreement)}
                          >
                            {t('fleet.declare.action')}
                          </button>
                        ) : null}
                        {canManage ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            aria-label={t('fleet.agreements.editNamed', {
                              title: agreement.title,
                            })}
                            onClick={() => setEditing(agreement)}
                          >
                            {t('fleet.actions.edit')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {expanded ? <DeclarationsPanel agreementId={expanded} /> : null}

      {adding ? (
        <AgreementDialog
          agreement={null}
          units={units.data ?? []}
          parties={suppliers}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {editing ? (
        <AgreementDialog
          agreement={editing}
          units={units.data ?? []}
          parties={suppliers}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {declaringOn ? (
        <DeclareDialog agreement={declaringOn} onClose={() => setDeclaringOn(null)} />
      ) : null}
    </>
  );
}

/**
 * The vehicles declared onto one agreement.
 *
 * Its own card below the table rather than an expanding row, because a
 * declaration has six columns of its own and nesting a table inside a cell
 * produces something no screen reader can navigate.
 */
function DeclarationsPanel({ agreementId }: { agreementId: string }) {
  const { t, i18n } = useTranslation();
  const { canAny } = useSession();
  const withdraw = useWithdrawAssignment();
  const query = useAllFleetAssignments({ agreementId });

  if (query.isPending) return <LoadingState rows={3} />;
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const declarations = query.items;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{t('fleet.declarations.title')}</h2>
      </div>
      <div className="card-body card-body--flush">
        {declarations.length === 0 ? (
          <EmptyState body={t('fleet.declarations.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('fleet.vehicles.plate')}</th>
                  <th scope="col">{t('fleet.vehicles.category')}</th>
                  <th scope="col">{t('fleet.declare.startsOn')}</th>
                  <th scope="col">{t('fleet.declare.endsOn')}</th>
                  <th scope="col">{t('fleet.declarations.verification')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('fleet.vehicles.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {declarations.map((declaration) => (
                  <tr key={declaration.id}>
                    <td>{declaration.registrationPlate}</td>
                    <td>{declaration.facts.category}</td>
                    <td>{formatDate(declaration.startsOn, i18n.language)}</td>
                    <td>
                      {declaration.endsOn
                        ? formatDate(declaration.endsOn, i18n.language)
                        : t('fleet.declarations.stillServing')}
                    </td>
                    <td>
                      <VerificationBadge state={declaration.verification} />
                      {declaration.verificationNote ? (
                        <div className="cell-secondary">{declaration.verificationNote}</div>
                      ) : null}
                    </td>
                    <td>
                      {canAny('fleet.disclose') ? (
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={withdraw.isPending}
                          aria-label={t('fleet.declarations.withdrawNamed', {
                            plate: declaration.registrationPlate,
                          })}
                          onClick={() => withdraw.mutate(declaration.id)}
                        >
                          {t('fleet.declarations.withdraw')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- compliance */

/**
 * The report.
 *
 * The grouping selector is the feature, and the rule it enforces is not
 * cosmetic: grouped by category the rows carry targets and a verdict, and
 * grouped by unit or agreement they carry contributions and no target at all.
 * The table reads `kind` from the row rather than from the selector, so the two
 * cannot come apart.
 */
function ComplianceTab() {
  const { t, i18n } = useTranslation();
  const [periodId, setPeriodId] = useState<string>(REFERENCE_PERIODS[1].id);
  const [groupBy, setGroupBy] = useState<ComplianceGrouping>('category');
  const query = useFleetCompliance({ period: periodId, groupBy });

  if (query.isPending) return <LoadingState rows={4} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const report = query.data;
  const isCompliance = groupBy === 'category';

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">{t('fleet.tabs.compliance')}</h2>
        <div className="card-actions">
          <label className="fleet-control" htmlFor="fleet-period">
            <span>{t('fleet.compliance.period')}</span>
            <select
              id="fleet-period"
              className="select"
              value={periodId}
              onChange={(event) => setPeriodId(event.target.value)}
            >
              {REFERENCE_PERIODS.map((period) => (
                <option key={period.id} value={period.id}>
                  {t(period.labelKey)}
                </option>
              ))}
            </select>
          </label>

          <label className="fleet-control" htmlFor="fleet-group-by">
            <span>{t('fleet.compliance.groupBy')}</span>
            <select
              id="fleet-group-by"
              className="select"
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as ComplianceGrouping)}
            >
              <option value="category">{t('fleet.compliance.byCategory')}</option>
              <option value="procurement_unit">{t('fleet.compliance.byUnit')}</option>
              <option value="agreement">{t('fleet.compliance.byAgreement')}</option>
            </select>
          </label>
        </div>
      </div>

      <div className="card-body">
        {report.provisional && report.provisionalReasonKey ? (
          <p role="note" className="fleet-note fleet-note--warning">
            {t(report.provisionalReasonKey)}
          </p>
        ) : null}

        {!isCompliance ? (
          <p className="cell-secondary">{t('fleet.compliance.contributionNote')}</p>
        ) : null}

        {report.groups.length === 0 ? (
          <EmptyState body={t('components.fleetCompliance.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">
                    {isCompliance ? t('fleet.columns.attainment') : t('fleet.columns.contribution')}
                  </th>
                  <th scope="col" className="numeric">
                    {t('fleet.columns.total')}
                  </th>
                  <th scope="col" className="numeric">
                    {t('fleet.columns.clean')}
                  </th>
                  <th scope="col">{t('fleet.columns.share')}</th>
                  {isCompliance ? (
                    <>
                      <th scope="col" className="numeric">
                        {t('fleet.columns.target')}
                      </th>
                      <th scope="col" className="numeric">
                        {t('fleet.columns.attainment')}
                      </th>
                    </>
                  ) : (
                    <th scope="col" className="numeric">
                      {t('fleet.columns.shareOfTotal')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {report.groups.map((group) => (
                  <tr key={`${group.key}-${group.categoryGroup}`}>
                    <td>
                      <strong>{group.labelKey ? t(group.labelKey) : group.label}</strong>
                      {!isCompliance ? (
                        <div className="cell-secondary">
                          {t(`fleet.categoryGroup.${group.categoryGroup}`)}
                        </div>
                      ) : null}
                    </td>
                    <td className="numeric">{formatNumber(group.counts.total, i18n.language)}</td>
                    <td className="numeric">{formatNumber(group.counts.clean, i18n.language)}</td>
                    <td>
                      {/* The bar is the point: three percentages in a column are
                          three numbers to compare by reading, and a bar with a
                          target notch is one to compare by looking. */}
                      <GroupShareBar group={group} locale={i18n.language} />
                    </td>
                    {group.kind === 'compliance' && group.target ? (
                      <>
                        <td className="numeric">
                          {formatNumber(group.target.minimumCleanPercent, i18n.language, {
                            maximumFractionDigits: 1,
                          })}
                          %
                        </td>
                        <td className="numeric">
                          {group.target.met === null ? (
                            <span className="cell-secondary">
                              {t('fleet.compliance.noProcurements')}
                            </span>
                          ) : (
                            <span
                              className={`status-badge status-badge--${
                                group.target.met ? 'success' : 'failure'
                              }`}
                            >
                              <span className="status-badge__glyph" aria-hidden="true">
                                {group.target.met ? '●' : '■'}
                              </span>
                              {`${group.target.cleanAttainmentPoints > 0 ? '+' : ''}${formatNumber(
                                group.target.cleanAttainmentPoints,
                                i18n.language,
                                { maximumFractionDigits: 1 },
                              )} pp`}
                            </span>
                          )}
                        </td>
                      </>
                    ) : null}
                    {group.kind === 'contribution' ? (
                      <td className="numeric">
                        {formatNumber(group.shareOfDenominatorPercent ?? 0, i18n.language, {
                          maximumFractionDigits: 1,
                        })}
                        %
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card-footer">
        <span className="cell-secondary">
          {t('fleet.compliance.footnote', {
            inScope: report.agreementsInScope,
            outOfScope: report.agreementsOutOfScope,
            noDate: report.agreementsWithoutAwardDate,
          })}
        </span>
      </div>
    </div>
  );
}
